/**
 * @fileoverview Primary contact phone, cellphone, and extension fields.
 */

import React from "react";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import CountryCodeSelector from "../../CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../../data/selectOptions";
import { getSelectedCountryCodeOption } from "../../../utils/common";
import { SHOW_IN_PROFILE_KEY } from "./contactsDataHelpers";
import styles from "./ContactsData.module.css";

const PrimaryPhoneField = ({ form }) => {
  const {
    t,
    isTablet,
    telefono,
    setTelefono,
    selectedCountryCode,
    setSelectedCountryCode,
    showPhone,
    setShowPhone,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
  } = form;
  const countryCodeOptions = getCountryCodeOptions(t);

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.phoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState("contacto1Telefono", telefono) === "error"
            ? styles.phoneInputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedCountryCode,
          )}
          onChange={(value) => {
            setSelectedCountryCode(value.value);
            handleInputChange("contacto1TelefonoCodigoPais", value.value);
          }}
          containerWidth={isTablet ? "85%" : "115%"}
          placeholder={t(
            "components.contactInformation.countryCodeSelectorPlaceholder",
          )}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t("components.contactsData.phonePlaceholder")}
          onChange={(e) => {
            const value = e.target.value;
            setTelefono(value);
            handleValidatedFieldChange("contacto1Telefono", value);
          }}
          value={telefono}
        />
      </div>
      {getErrorMessage("contacto1Telefono") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("contacto1Telefono")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowPhone(!showPhone);
            handleInputChange("contacto1MostrarTelefono", !showPhone);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showPhone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showPhone ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const PrimaryCellphoneField = ({ form }) => {
  const {
    t,
    isTablet,
    celular,
    setCelular,
    selectedCellphoneCountryCode,
    setSelectedCellphoneCountryCode,
    showCellphone,
    setShowCellphone,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
  } = form;
  const countryCodeOptions = getCountryCodeOptions(t);

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.cellphoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState("contacto1Celular", celular) === "error"
            ? styles.phoneInputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedCellphoneCountryCode,
          )}
          onChange={(value) => {
            setSelectedCellphoneCountryCode(value.value);
            handleInputChange("contacto1CelularCodigoPais", value.value);
          }}
          containerWidth={isTablet ? "85%" : "115%"}
          placeholder={t(
            "components.contactInformation.countryCodeSelectorPlaceholder",
          )}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t("components.contactsData.phonePlaceholder")}
          onChange={(e) => {
            const value = e.target.value;
            setCelular(value);
            handleValidatedFieldChange("contacto1Celular", value);
          }}
          value={celular}
        />
      </div>
      {getErrorMessage("contacto1Celular") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("contacto1Celular")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowCellphone(!showCellphone);
            handleInputChange("contacto1MostrarCelular", !showCellphone);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showCellphone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showCellphone ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const PrimaryExtensionField = ({ form }) => {
  const {
    t,
    extension,
    setExtension,
    showExtension,
    setShowExtension,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.extensionLabel")}
      </label>
      <input
        type="number"
        className={`${styles.input} ${
          getFieldValidationState("contacto1Extension", extension) === "error"
            ? styles.inputError
            : ""
        }`}
        placeholder={t("components.contactsData.extensionPlaceholder")}
        onChange={(e) => {
          const value = e.target.value;
          if (value.length <= 5) {
            setExtension(value);
            handleValidatedFieldChange("contacto1Extension", value);
          }
        }}
        value={extension}
      />
      {getErrorMessage("contacto1Extension") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("contacto1Extension")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowExtension(!showExtension);
            handleInputChange("contacto1MostrarExtension", !showExtension);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showExtension ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showExtension ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

export const PrimaryContactPhoneFields = ({ form }) => (
  <>
    <PrimaryPhoneField form={form} />
    <PrimaryCellphoneField form={form} />
    <PrimaryExtensionField form={form} />
  </>
);
